import * as React from "preact";
import { FunctionComponent } from "preact";
import { useEffect, useState } from "preact/hooks";

import TableHead from "../TableHead";
import TableBody from "../TableBody";
import Pagination from "../Pagination";
import TableFilter from "../TableFilter";
import FilterData from "../../CashHubTable/Components/FilterData";

interface ITable {
  columns: ITableColumns[];
  data: any;
  pageSize: number;
  enableFiltering: boolean;
  filterFields: IFilter[];
  defaultFilter: string;
}

export interface ITableColumns {
  label: string;
  accessor: string;
  type: string;
  hiddenOn?: string;
  shownOn?: string;
}

export interface IFilter {
  filterField: string;
  filterLabel: string;
  filterProperties: IFilterProperties[];
}

export interface IFilterProperties {
  filterDisplay: string;
  fieldsToShow: string;
}

const Table: FunctionComponent<ITable> = ({
  columns,
  data,
  pageSize,
  enableFiltering,
  filterFields,
  defaultFilter,
}) => {
  const [filterProperties, setFilterProperties] = useState(filterFields);
  const [displayData, setDisplayData] = useState(data);
  const [firstRun, setFirstRun] = useState(false);
  const [columnData] = useState(columns);
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState(data);
  const [currentSortField, setCurrentSortField] = useState("aerRate");
  const [currentSortDirection, setCurrentSortDirection] = useState("desc");
  const [currentFilter, setCurrentFilter] = useState("ALL");

  const [allData] = useState(data);
  const totalHits = tableData.length;

  useEffect(() => {
    handleSorting("aerRate", "desc");
    if (!firstRun) {
      if (defaultFilter && defaultFilter != "All") {
        const filterParams = FilterData[0].filterProperties.find(
          (x) => x.filterDisplay.toLowerCase() === defaultFilter.toLowerCase()
        );
        filterParams?.fieldsToShow && filterCurrentTable("aerRate", "desc", filterParams.fieldsToShow);
      }
    }
    setFirstRun(true);
  }, []);

  useEffect(() => {
    const filterList = filterFields.map((item) => {
      return {
        filterField: item.filterField,
        filterLabel: item.filterLabel,
        filterProperties: item.filterProperties,
      };
    });

    setFilterProperties(filterList);
  }, [filterFields]);

  useEffect(() => {
    setCurrentPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    handleChangePage(1);
  }, [tableData]);

  const handleChangePage = (page: number): void => {
    const newPage = getPage(page);
    setDisplayData(newPage);
    setCurrentPage(page);
  };

  const getPage = (pageNumber: number) => {
    const page = tableData.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
    return page;
  };

  const handleFilter = (e): void => {
    const filterOptions = e.target.value;
    setCurrentFilter(filterOptions);
    if (filterOptions === "All") {
      const allFilteredData = handleSortingData(currentSortField, currentSortDirection, getAllData());

      setTableData(allFilteredData);
      const page = getPage(1);
      setDisplayData(page);
    } else {
      filterCurrentTable(currentSortField, currentSortDirection, filterOptions);
    }
  };

  const filterCurrentTable = (currentSortField, currentSortDirection, filterOptions) => {
    const filterProperties = filterOptions.split(",");
    const filteredData = handleSortingData(
      currentSortField,
      currentSortDirection,
      buildFilteredArray(filterProperties, allData)
    );
    setTableData(filteredData);
    const page = getPage(1);
    setDisplayData(page);
  };

  const getAllData = () => {
    return allData;
  };

  const buildFilteredArray = (parameters: any[], data: any[]) => {
    const array: any[] = [];
    data?.forEach(item => {
      if (parameters.some(param => item.accountHolderTypes.includes(param))) {
        array.push(item);
      }
    })
    return array;
  };

  const handleSorting = (sortField, sortOrder) => {
    setCurrentSortDirection(sortOrder);
    setCurrentSortField(sortField);
    if (sortField) {
      const sorted = handleSortingData(sortField, sortOrder, tableData);
      setTableData(sorted);
    }
  };

  const handleSortingData = (sortField, sortOrder, data) => {
    if (sortField) {
      const sorted = [...data].sort((a, b) => {
        if (a[sortField] === null) return 1;
        if (b[sortField] === null) return -1;
        if (a[sortField] === null && b[sortField] === null) return 0;
        return (
          a[sortField].toString().localeCompare(b[sortField].toString(), "en", {
            numeric: true,
          }) * (sortOrder === "asc" ? 1 : -1)
        );
      });
      return sorted;
    }
  };

  return (
    <div>
      <div className="sortable-table--filters">
        {enableFiltering &&
          filterProperties.map((item) => (
            <TableFilter
              key={item.filterField}
              filter={item}
              handleFilter={handleFilter}
              defaultFilter={defaultFilter}
            ></TableFilter>
          ))}
      </div>
      <div className="container container--scrollable">
        <table className="sortable-table table--responsive">
          <TableHead
            columns={columnData}
            handleSorting={handleSorting}
            currentFilter={currentFilter}
          />
          <TableBody
            columns={columnData}
            data={displayData}
            currentFilter={currentFilter}
          />
        </table>
      </div>
      <Pagination
        page={currentPage}
        pageSize={pageSize}
        totalItems={totalHits}
        handleChangePage={handleChangePage}
        className="search-results__pagination"
      ></Pagination>
    </div>
  );
};

export default Table;
